window.AdminLayout = function AdminLayout({ user, onLogout }) {
  const [activeTab, setActiveTab] = React.useState('dashboard');
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = React.useState('');
  const [globalSearchResults, setGlobalSearchResults] = React.useState(null);
  const [isSearching, setIsSearching] = React.useState(false);
  const [toastNotification, setToastNotification] = React.useState(null);

  const showNotification = (message, type = 'success') => {
    setToastNotification({ message, type });
    setTimeout(() => setToastNotification(null), 4000);
  };

  const handleGlobalSearch = async (query) => {
    setGlobalSearchQuery(query);
    if (!query || query.length < 2) {
      setGlobalSearchResults(null);
      return;
    }
    setIsSearching(true);
    try {
      const res = await window.apiClient.get('search.php', { q: query });
      if (res.success) {
        setGlobalSearchResults(res.results || []);
      }
    } catch (e) {
      setGlobalSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
    { id: 'industries', label: 'Industries & Sectors', icon: 'factory' },
    { id: 'products', label: 'Products catalog', icon: 'inventory_2' },
    { id: 'home_features', label: 'Home Features', icon: 'featured_play_list' },
    { id: 'content', label: 'Website Content & Pages', icon: 'edit_note' },
    { id: 'categories', label: 'Categories', icon: 'category' },
    { id: 'inquiries', label: 'Inquiries & RFQs', icon: 'mail' },
    { id: 'blogs', label: 'Insights & Technical Hub', icon: 'newspaper' },
    { id: 'careers', label: 'Job Openings', icon: 'work' },
    { id: 'applications', label: 'Applications', icon: 'badge' },
    { id: 'newsletter', label: 'Newsletter Broadcast', icon: 'campaign' },
    { id: 'media', label: 'Media Library', icon: 'perm_media' },
    { id: 'users', label: 'User Management', icon: 'group' },
    { id: 'logs', label: 'Activity Logs', icon: 'history' }
  ];

  return (
    <div className="h-screen bg-slate-100 text-slate-800 flex flex-col font-sans selection:bg-orange-500 selection:text-white relative">
      
      {/* Toast Notification Container */}
      {toastNotification && (
        <div className="fixed top-5 right-5 z-50 animate-fadeIn max-w-sm sm:max-w-md w-full px-4">
          <div className={`p-4 rounded-xl shadow-2xl border text-xs sm:text-sm font-sans flex items-center gap-3 backdrop-blur-md ${
            toastNotification.type === 'success' 
              ? 'bg-emerald-50 border-emerald-300 text-emerald-800' 
              : 'bg-red-50 border-red-300 text-red-800'
          }`}>
            <span className="material-symbols-outlined text-xl shrink-0">
              {toastNotification.type === 'success' ? 'check_circle' : 'error'}
            </span>
            <span className="font-medium leading-normal">{toastNotification.message}</span>
          </div>
        </div>
      )}

      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 h-16 flex items-center px-3 sm:px-6 justify-between gap-2 sm:gap-4 shadow-sm">
        
        {/* Left Brand */}
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <button 
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            className="lg:hidden text-slate-600 hover:text-slate-900 p-1 rounded hover:bg-slate-100"
            aria-label="Toggle Sidebar"
          >
            <span className="material-symbols-outlined text-2xl">menu</span>
          </button>
          
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => setActiveTab('dashboard')}>
            <div className="bg-white p-1 rounded-lg border border-slate-200 shadow-sm shrink-0">
              <img src="/logo.png" alt="NOVATEK" className="h-5 sm:h-6 w-auto" />
            </div>
            <span className="font-serif font-bold text-base sm:text-lg text-slate-900 tracking-wide hidden xs:inline sm:inline">
              NOVATEK <span className="bg-secondary/10 text-secondary border border-secondary/20 px-2 py-0.5 rounded font-mono text-xs hidden md:inline">Admin Portal</span>
            </span>
          </div>
        </div>

        {/* Center Global Search Bar */}
        <div className="relative flex-grow max-w-xs sm:max-w-md">
          <div className="relative">
            <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-base">
              search
            </span>
            <input
              type="text"
              placeholder="Quick search products, inquiries, applications..."
              value={globalSearchQuery}
              onChange={(e) => handleGlobalSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-secondary transition-colors"
            />
          </div>

          {/* Quick Search Overlay Results */}
          {globalSearchResults && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-80 overflow-y-auto z-50 p-2 space-y-1 animate-fadeIn">
              <div className="flex justify-between items-center px-2 py-1 border-b border-slate-100 text-[10px] font-mono text-slate-400">
                <span>SEARCH RESULTS ({globalSearchResults.length})</span>
                <button onClick={() => setGlobalSearchResults(null)} className="hover:text-slate-800 font-bold">CLOSE</button>
              </div>
              {globalSearchResults.length === 0 ? (
                <div className="p-4 text-center text-xs text-slate-500">No matching records found.</div>
              ) : (
                globalSearchResults.map((res, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      if (res.type === 'product') setActiveTab('products');
                      else if (res.type === 'career') setActiveTab('careers');
                      else if (res.type === 'application') setActiveTab('applications');
                      else if (res.type === 'inquiry') setActiveTab('inquiries');
                      setGlobalSearchResults(null);
                    }}
                    className="p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer flex items-center justify-between transition-colors border border-transparent hover:border-slate-200"
                  >
                    <div className="min-w-0 pr-2">
                      <div className="text-xs font-bold text-slate-900 truncate">{res.title}</div>
                      <div className="text-[10px] font-mono text-slate-500 truncate">{res.subtitle}</div>
                    </div>
                    <span className="font-mono text-[9px] uppercase px-2 py-0.5 rounded bg-slate-100 text-secondary border border-slate-200 shrink-0 font-bold">
                      {res.type}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Right User Options */}
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <div className="hidden md:flex items-center gap-2 border-r border-slate-200 pr-3">
            <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
            <span className="font-mono text-xs text-slate-700 font-bold truncate max-w-[140px]">{user?.full_name || 'Admin Officer'}</span>
          </div>

          <a 
            href="#home" 
            onClick={() => window.location.hash = ''} 
            className="text-slate-600 hover:text-slate-900 text-xs font-mono flex items-center gap-1 bg-slate-100 hover:bg-slate-200 px-2.5 py-1.5 rounded-lg transition-colors border border-slate-200"
            title="Return to Public Website"
          >
            <span className="material-symbols-outlined text-sm">visibility</span>
            <span className="hidden sm:inline">View Website</span>
          </a>

          <button
            onClick={onLogout}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1.5 rounded-lg transition-colors flex items-center gap-1 text-xs font-mono border border-transparent hover:border-red-200"
            title="Log Out"
          >
            <span className="material-symbols-outlined text-lg">logout</span>
          </button>
        </div>
      </header>

      {/* Main Body */}
      <div className="flex flex-1 overflow-hidden relative">
        
        {/* Mobile Backdrop */}
        {mobileSidebarOpen && (
          <div 
            onClick={() => setMobileSidebarOpen(false)}
            className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-xs lg:hidden animate-fadeIn"
          />
        )}

        {/* Sidebar */}
        <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform lg:transform-none transition-transform duration-300 flex flex-col justify-between shadow-sm ${
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}>
          <div className="p-4 space-y-1 overflow-y-auto">
            <div className="flex justify-between items-center px-3 py-2 text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">
              <span>MANAGEMENT MODULES</span>
              <button onClick={() => setMobileSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-slate-800">
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </div>
            {navItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setMobileSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl font-medium text-xs transition-all cursor-pointer ${
                    isActive 
                      ? 'bg-secondary text-white shadow-md font-bold' 
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <span className="material-symbols-outlined text-lg">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* System Footer Bar */}
          <div className="p-4 border-t border-slate-100 text-[10px] font-mono text-slate-400 space-y-0.5 bg-slate-50/50">
            <div>NOVATEK Industrial Systems</div>
            <div className="text-emerald-600 font-bold flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span> Live Portal Operational
            </div>
          </div>
        </aside>

        {/* Content Area */}
        <main className="flex-1 bg-slate-50 overflow-y-auto p-3 sm:p-6 lg:p-8 w-full max-w-full">
          {activeTab === 'dashboard' && <window.AdminDashboard setActiveTab={setActiveTab} showNotification={showNotification} />}
          {activeTab === 'home_features' && <window.AdminHomeFeatures showNotification={showNotification} />}
          {activeTab === 'industries' && <window.AdminIndustries showNotification={showNotification} />}
          {activeTab === 'products' && <window.AdminProducts showNotification={showNotification} />}
          {activeTab === 'categories' && <window.AdminCategories showNotification={showNotification} />}
          {activeTab === 'careers' && <window.AdminCareers showNotification={showNotification} />}
          {activeTab === 'applications' && <window.AdminApplications showNotification={showNotification} />}
          {activeTab === 'inquiries' && <window.AdminInquiries showNotification={showNotification} />}
          {activeTab === 'blogs' && <window.AdminBlogs showNotification={showNotification} />}
          {activeTab === 'newsletter' && <window.AdminNewsletter showNotification={showNotification} />}
          {activeTab === 'media' && <window.AdminMedia showNotification={showNotification} />}
          {activeTab === 'content' && <window.AdminContent showNotification={showNotification} />}
          {activeTab === 'users' && <window.AdminUsers user={user} showNotification={showNotification} />}
          {activeTab === 'logs' && <window.AdminLogs showNotification={showNotification} />}
        </main>

      </div>

    </div>
  );
};
